import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { LoadingIndicator, PrivateLayout, RequestSubmitMessage, NotificationToastMessage } from '../components';
import { rxModulesMetadata, useListRequest } from '../hooks';
import { AnyType, PhoneNumber, ServiceOptionType } from '../interfaces';
import {
  AppRootState,
  navigateToCases,
  navigateToHome,
  navigateToServiceRequest,
  navigateToServiceRequestSelected,
  useTenancyDrugListSelector,
  tenancyFeatureSelector
} from '../redux';
import {
  GATEWAY_API_URL,
  STANDARD_PORTAL_APPLICATION_NAME,
  useEnrollment,
  ENROLLMENT_MODULE_TRIGGER_RULE
} from '../api';
import { AnyObject } from '@data-driven-forms/react-form-renderer';
import memoizeOne from 'memoize-one';
import { getToken } from '../components/enrollment-module';
import { clientApplicationNameSelector } from '../utils';
import { Drug } from '../api/portal-config.generated';

const renderSwitch = (param: string) => {
  switch (param) {
    case 'BV Complete -PA Required':
      return ServiceOptionType.MedicalElectronicPriorAuthorization;
    case 'Pre-determination Option: Medical':
      return ServiceOptionType.MedicalElectronicPriorAuthorization;
    case 'Product Covered - PA Required':
      return ServiceOptionType.PharmacyElectronicPriorAuthorization;
    case 'Product Covered with restrictions - PA Required':
      return ServiceOptionType.PharmacyElectronicPriorAuthorization;
    default:
      return '';
  }
};

export const getEnrollmentModuleAPIEndpoint = (enrollmentId: string | null, practiceId: string | null): string => {
  return `${GATEWAY_API_URL}/enrollment/${enrollmentId}/${practiceId}`?.replace(/([^:]\/)\/+/g, '$1');
};

export const getAPIEndpointForEnrollmentModulePDF = (
  enrollmentId: string | null,
  practiceId: string | null
): string => {
  return `${GATEWAY_API_URL}/enrollment/pdf/${enrollmentId}/${practiceId}`?.replace(/([^:]\/)\/+/g, '$1');
};

const getOkToLeaveMessageValue = (phoneNumbers: Array<PhoneNumber>, phoneType: string) => {
  if (phoneNumbers?.length == 0) return false;
  return phoneNumbers?.filter((p) => p.phoneType.toLowerCase() === phoneType.toLowerCase())[0]?.okToLeaveMessage;
};

const getIndividualInsurance = (insuranceList: Array<AnyObject>, type: string) => {
  const selectedInsurance = insuranceList?.filter((i) => i.medicalInsuranceType.toLowerCase() === type.toLowerCase());
  return selectedInsurance?.length > 0 ? selectedInsurance[0] : {};
};

const getPatientInsurancePayload = (insuranceData: AnyObject) => {
  const length = insuranceData?.length;
  if (length < 1) return [];
  const primaryInsurance = getIndividualInsurance(insuranceData?.medicalInsurances, 'primary');
  const secondaryInsurance = getIndividualInsurance(insuranceData?.medicalInsurances, 'secondary');
  const tertiaryInsurance = getIndividualInsurance(insuranceData?.medicalInsurances, 'tertiary');
  return [
    {
      patientInsuranceId: parseInt(sessionStorage.getItem('primaryInsId') ?? '0'),
      insuranceType: 'PRIMARY',
      medicalCardHolderId: primaryInsurance?.cardHolderId || null,
      medicalGroup: primaryInsurance?.groupNumber || null,
      medicalPhoneNumber: primaryInsurance?.phoneNumber?.phoneNumber || null,
      medicalPlanName: primaryInsurance?.planName || null,
      pharmacyBINNumber: insuranceData?.pharmacyInsurance?.bin || null,
      pharmacyCardholderID: insuranceData?.pharmacyInsurance?.cardHolderId || null,
      pharmacyGroup: insuranceData?.pharmacyInsurance?.groupNumber || null,
      pharmacyPCN: insuranceData?.pharmacyInsurance?.pcn || null,
      pharmacyPhoneNumber: insuranceData?.pharmacyInsurance?.phoneNumber?.phoneNumber || null,
      pharmacyPlanName: insuranceData?.pharmacyInsurance?.insuranceName || null,
      pbmName: insuranceData?.pharmacyInsurance?.planName || null,
      policyHolderName: insuranceData?.pharmacyInsurance?.policyHolderName || null,
      subscriberRelationshipToCardholder: primaryInsurance?.cardHolderRelationship || null,
      cardHolderName: primaryInsurance?.cardHolderName || null,
      cardholderDOB: primaryInsurance?.cardholderDOB || null,
      employer: primaryInsurance?.employer || null,
      planType: primaryInsurance?.planType || null,
      PayerId: parseInt(primaryInsurance?.PayerId ?? '0')
    },
    {
      patientInsuranceId: parseInt(sessionStorage.getItem('secondaryInsId') ?? '0'),
      insuranceType: 'SECONDARY',
      medicalCardHolderId: secondaryInsurance?.cardHolderId || null,
      medicalGroup: secondaryInsurance?.groupNumber || null,
      medicalPhoneNumber: secondaryInsurance?.phoneNumber?.phoneNumber || null,
      medicalPlanName: secondaryInsurance?.planName || null,
      pharmacyBINNumber: insuranceData?.pharmacyInsurance?.bin || null,
      pharmacyCardholderID: insuranceData?.pharmacyInsurance?.cardHolderId || null,
      pharmacyGroup: insuranceData?.pharmacyInsurance?.groupNumber || null,
      pharmacyPCN: insuranceData?.pharmacyInsurance?.pcn || null,
      pharmacyBIN: insuranceData?.pharmacyInsurance?.bin || null,
      pharmacyPhoneNumber: insuranceData?.pharmacyInsurance?.phoneNumber?.phoneNumber || null,
      pharmacyPlanName: insuranceData?.pharmacyInsurance?.insuranceName || null,
      pbmName: insuranceData?.pharmacyInsurance?.planName || null,
      subscriberRelationshipToCardholder: secondaryInsurance?.cardHolderRelationship || null,
      cardHolderName: secondaryInsurance?.cardHolderName || null,
      cardholderDOB: secondaryInsurance?.cardholderDOB || null,
      employer: secondaryInsurance?.employer || null,
      planType: secondaryInsurance?.planType || null,
      PayerId: parseInt(secondaryInsurance?.PayerId ?? '0')
    },
    {
      patientInsuranceId: parseInt(sessionStorage.getItem('tertiaryInsId') ?? '0'),
      insuranceType: 'TERTIARY',
      medicalCardHolderId: tertiaryInsurance?.cardHolderId || null,
      medicalGroup: tertiaryInsurance?.groupNumber || null,
      medicalPhoneNumber: tertiaryInsurance?.phoneNumber?.phoneNumber || null,
      medicalPlanName: tertiaryInsurance?.planName || null,
      pharmacyBINNumber: insuranceData?.pharmacyInsurance?.bin || null,
      pharmacyCardholderID: insuranceData?.pharmacyInsurance?.cardHolderId || null,
      pharmacyGroup: insuranceData?.pharmacyInsurance?.groupNumber || null,
      pharmacyPCN: insuranceData?.pharmacyInsurance?.pcn || null,
      pharmacyPhoneNumber: insuranceData?.pharmacyInsurance?.phoneNumber?.phoneNumber || null,
      pharmacyPlanName: insuranceData?.pharmacyInsurance?.planName || null,
      pbmName: insuranceData?.pharmacyInsurance?.planName || null,
      subscriberRelationshipToCardholder: tertiaryInsurance?.cardHolderRelationship || null,
      cardHolderName: tertiaryInsurance?.cardHolderName || null,
      employer: tertiaryInsurance?.employer || null,
      planType: tertiaryInsurance?.planType || null,
      PayerId: parseInt(tertiaryInsurance?.PayerId ?? '0')
    }
  ];
};

const getAdditionalPhysiciansPayload = (additionalPhysiciansData: Array<AnyObject>) => {
  return additionalPhysiciansData?.map((pd: AnyObject) => {
    return {
      firstName: pd?.person?.firstName,
      lastName: pd?.person?.lastName,
      middleName: pd?.person?.middleName,
      npi: pd?.npi,
      taxId: pd?.taxId,
      dea: pd?.deaNumber,
      ptan: pd?.ptanNumber,
      stateLicenseNumber: pd?.stateLicenseNumber ?? '',
      licenseState: pd?.licensingState,
      specialty: pd?.specialty,
      physicianType: pd?.physicianType,
      medicareId: pd?.medicaidMedicareProviderNumber
    };
  });
};
const getManualAttestation = (doc: Array<AnyObject>) => {
  if (doc?.length < 1) return {};
  const attestation = doc.find((d) => d?.entityType === 'Enrollment.PatientConsent.Attestation');
  if (attestation) {
    return {
      fileName: attestation.fileName,
      fileData: attestation.fileContent
    };
  } else {
    return {};
  }
};
export const prepareEnollmentPayload = (
  data: AnyObject,
  caseId: number | null,
  caseRecordType: string | null,
  drugId: number,
  document?: string | null,
  isUsingEnrollmentModule?: boolean,
  isTamTracker?: boolean
) => {
  const doc = document
    ? {
        fileName: 'enrollmentdocumentfromModule',
        fileData: document
      }
    : {};
  const locationId = sessionStorage.getItem('locationId');
  const getPhoneNumber = (obj: any, phoneType: string) => {
    const phoneObj = obj?.find((p: AnyObject) => p.phoneType.toLowerCase() === phoneType);
    return phoneObj && phoneObj?.phoneNumber ? phoneObj.phoneNumber : '';
  };
  const getMedicationNotes = (obj: any) => {
    return obj?.find((obj: { name: string }) => obj.name === 'Clinical.ListOtherMedications.Specify')?.value ?? null;
  };
  const getCustomFieldData = (obj: any, fieldName: string) => {
    const fieldObj = obj?.find((p: AnyObject) => p.name === fieldName);
    return fieldObj && fieldObj?.value ? fieldObj?.value : null;
  };
  // The infusion site comes in two tag families: Clinical.SiteInfusion.* (the shipping site fields) and Clinical.InfusionSite*
  // (the Infusion Site boxes, named after the tags of their PDF). Either one fills the same siteInfusion* fields of the request,
  // and the first family wins (CDPR-52713).
  const getSiteInfusionData = (shippingSiteTag: string, infusionSiteTag: string) =>
    getCustomFieldData(data?.customFields, shippingSiteTag) || getCustomFieldData(data?.customFields, infusionSiteTag);

  const getFrequencyData = (obj: any) => {
    let frequency = '';
    if (getCustomFieldData(data?.customFields, 'clinicalFields.clinicalDoseFrequency.2weeks') === 'Yes') {
      frequency = 'Once every 2 weeks';
    } else if (getCustomFieldData(data?.customFields, 'clinicalFields.clinicalDoseFrequency.4weeks') === 'Yes') {
      frequency = 'Once every 4 weeks';
    } else if (getCustomFieldData(data?.customFields, 'clinicalFields.clinicalDoseFrequency.other') === 'Yes') {
      frequency = 'Other';
    }
    return frequency;
  };

  const getDispenseData = (obj: any) => {
    const dispenseOptions: string[] = [];
    if (getCustomFieldData(data?.customFields, 'Clinical.clinicalDispenseVials.1Month') === 'Yes') {
      dispenseOptions.push('1 Month');
    }
    if (getCustomFieldData(data?.customFields, 'Clinical.clinicalDispenseVials.2Month') === 'Yes') {
      dispenseOptions.push('2 Month');
    }
    if (getCustomFieldData(data?.customFields, 'Clinical.clinicalDispenseVials.3Month') === 'Yes') {
      dispenseOptions.push('3 Month');
    }
    if (getCustomFieldData(data?.customFields, 'Clinical.clinicalDispenseVials.Other') === 'Yes') {
      dispenseOptions.push('Other');
    }
    return dispenseOptions.join(',');
  };

  const getShippingAddressLocation = (obj: any) => {
    let shippingAddressLocation = '';
    if (getCustomFieldData(obj, 'Clinical.LocationOfInjection.Prescriberoffice') === 'Yes') {
      shippingAddressLocation = "Physician's office";
    } else if (getCustomFieldData(obj, 'Clinical.LocationOfInjection.Hospitaloutpatient') === 'Yes') {
      shippingAddressLocation = 'Hospital Outpatient Department';
    } else if (getCustomFieldData(obj, 'Clinical.LocationOfInjection.InfusionCenter') === 'Yes') {
      shippingAddressLocation = 'Alternate injection center';
    } else if (getCustomFieldData(obj, 'Clinical.LocationOfInjection.PatientsAddress') === 'Yes') {
      shippingAddressLocation = "Patient's address";
    }
    return shippingAddressLocation;
  };

  const getAcquireThrough = (obj: any) => {
    let acquireThrough = '';
    if (getCustomFieldData(obj, 'Clinical.Prescription.AcquireThrough.BuyAndBill') === 'Yes') {
      acquireThrough = 'Buy & Bill';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.AcquireThrough.SpecialtyPharmacy') === 'Yes') {
      acquireThrough = 'Specialty pharmacy';
    }
    return acquireThrough;
  };

  const getPrescriptionType = (obj: any) => {
    let prescriptionType = '';
    if (getCustomFieldData(obj, 'Clinical.Prescription.NewStart') === 'Yes') {
      prescriptionType = 'Naive/new start';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.ReStart') === 'Yes') {
      prescriptionType = 'Restart';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.LastInjectionDate') === 'Yes') {
      prescriptionType = 'Last injection date';
    }
    return prescriptionType;
  };

  const getPrescriptionDispense = (obj: any) => {
    let prescriptionDispense = '';
    if (getCustomFieldData(obj, 'Clinical.Prescription.Dispense.PFS.cb') === 'Yes') {
      prescriptionDispense = 'Single-dose prefilled syringe';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dispense.Autoinjection.cb') === 'Yes') {
      prescriptionDispense = 'Autoinjection (12 years and older)';
    }
    return prescriptionDispense;
  };

  const getPrescriptionDaysSupply = (obj: any) => {
    let daysSupply = '';
    if (getCustomFieldData(obj, 'Clinical.Prescription.DispenseQuantity.30Days') === 'Yes') {
      daysSupply = '30-day supply';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.DispenseQuantity.90Days') === 'Yes') {
      daysSupply = '90-day supply';
    }
    return daysSupply;
  };

  const getDosage2Weeks = (obj: any) => {
    let dosage = '';
    if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.150mg2week.cb') === 'Yes') {
      dosage = '150 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.225mg2week.cb') === 'Yes') {
      dosage = '225 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.300mg2week.cb') === 'Yes') {
      dosage = '300 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.375mg2week.cb') === 'Yes') {
      dosage = '375 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.450mg2week.cb') === 'Yes') {
      dosage = '450 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.525mg2week.cb') === 'Yes') {
      dosage = '525 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.600mg2week.cb') === 'Yes') {
      dosage = '600 mg';
    }
    return dosage;
  };

  const getDosage4Weeks = (obj: any) => {
    let dosage = '';
    if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.75mg4week.cb') === 'Yes') {
      dosage = '75 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.150mg4week.cb') === 'Yes') {
      dosage = '150 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.225mg4week.cb') === 'Yes') {
      dosage = '225 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.300mg4week.cb') === 'Yes') {
      dosage = '300 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.450mg4week.cb') === 'Yes') {
      dosage = '450 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.525mg4week.cb') === 'Yes') {
      dosage = '525 mg';
    } else if (getCustomFieldData(obj, 'Clinical.Prescription.Dosage.600mg4week.cb') === 'Yes') {
      dosage = '600 mg';
    }
    return dosage;
  };

  const getPatientWeight = (obj: any) => {
    const weightLB = obj?.weightLB ?? null;
    const weightKG = obj?.weightKG ?? null;
    if (weightKG != null && weightKG !== '') {
      return { weight: weightKG, weightMeasure: 'kg' };
    }
    if (weightLB != null && weightLB !== '') {
      return { weight: weightLB, weightMeasure: 'lb' };
    }
    return { weight: null, weightMeasure: null };
  };

  const patientWeight = getPatientWeight(data?.clinical);

  return {
    userName: data?.sourceSystem?.sourceSystemUser?.userName,
    enrollmentType: 'Patient Enrollment',
    caseRecordType: caseRecordType,
    supportServiceData: data?.serviceSpecific?.supportServiceData,
    patient: {
      patientId: parseInt(data?.patient?.externalId),
      firstName: data?.patient?.person?.firstName,
      middleName: data?.patient?.person?.middleName,
      lastName: data?.patient?.person?.lastName,
      last4SSN: data?.patient?.person?.last4SSN,
      dob: data?.patient?.dateOfBirth,
      gender: data?.patient?.person?.gender,
      preferredLanguage: data?.patient?.preferredLanguages ? data?.patient?.preferredLanguages[0]?.language : '',
      otherLanguage: data?.patient?.preferredLanguages
        ? data?.patient?.preferredLanguages[1]
          ? data?.patient?.preferredLanguages[1]?.language
          : ''
        : '',
      email: data?.patient?.email,
      patientPhoneNumbers: data?.patient?.phoneNumbers,
      okToLeaveMessageMobile: getOkToLeaveMessageValue(data?.patient?.phoneNumbers, 'home') || false,
      okToLeaveMessageHome: getOkToLeaveMessageValue(data?.patient?.phoneNumbers, 'mobile') || false,
      address1: data?.patient?.addresses?.[0]?.address1,
      address2: data?.patient?.addresses?.[0]?.address2,
      zip: data?.patient?.addresses?.[0]?.zip,
      city: data?.patient?.addresses?.[0]?.city,
      state: data?.patient?.addresses?.[0]?.state,
      hasCaregiver: data?.patient?.hasCaregiver,
      caregiver: {
        firstName: data?.patient?.caregiver?.person?.firstName,
        middleName: data?.patient?.caregiver?.person?.middleName,
        lastName: data?.patient?.caregiver?.person?.lastName,
        email: data?.patient?.caregiver?.email,
        homePhoneNumber: getPhoneNumber(data?.patient?.caregiver?.phoneNumbers, 'home'),
        mobilePhoneNumber: getPhoneNumber(data?.patient?.caregiver?.phoneNumbers, 'mobile'),
        relationship: data?.patient?.caregiver?.relationship,
        isLegallyAuthorizedRepresentative: data?.patient?.caregiver?.isLegallyAuthorizedRepresentative
      }
    },
    insurance: {
      hasPatientInsurance: data?.insurance?.hasInsurance,
      medicareBeneficiaryId: data?.insurance?.medicareId,
      patientInsurances: data?.insurance?.hasInsurance ? getPatientInsurancePayload(data?.insurance) : []
    },
    physicians: {
      lastName: data?.prescriber?.physician?.person?.lastName,
      firstName: data?.prescriber?.physician?.person?.firstName,
      middleName: data?.prescriber?.physician?.person?.middleName,
      npi: data?.prescriber?.physician?.npi,
      taxId: data?.prescriber?.physician?.taxId,
      stateLicenseNumber: data?.prescriber?.physician?.stateLicenseNumber,
      licenseState: data?.prescriber?.physician?.licensingState ?? '',
      medicareId: data?.prescriber?.physician?.medicaidMedicareProviderNumber,
      providerType: data?.prescriber?.physician?.physicianType,
      physicianId: parseInt(sessionStorage.getItem('physicianId') ?? '0'),
      hin: data?.prescriber?.physician?.hin,
      organization: {
        organizationId: parseInt(data?.practice?.externalId),
        name: data?.practice?.name,
        npi: data?.practice?.npi,
        taxId: data?.practice?.taxId,
        dea: data?.practice?.deaNumber,
        email: data?.practice?.email,
        role: data?.practice?.role,
        type: data?.practice?.type,
        organizationAddress: {
          organizationAddressId: parseInt(locationId ?? '0'),
          address1: data?.practice?.location?.address?.address1,
          address2: data?.practice?.location?.address?.address2,
          zip: data?.practice?.location?.address?.zip,
          city: data?.practice?.location?.address?.city,
          state: data?.practice?.location?.address?.state,
          phoneNumber: getPhoneNumber(data?.practice?.location?.phoneNumbers, 'work'),
          faxNumber: getPhoneNumber(data?.practice?.location?.phoneNumbers, 'fax')
        }
      },
      officeContact: {
        lastName: data?.practice?.officeContact?.person?.lastName,
        firstName: data?.practice?.officeContact?.person?.firstName,
        phoneNumber: getPhoneNumber(data?.practice?.officeContact?.phoneNumbers, 'work'),
        fax: getPhoneNumber(data?.practice?.officeContact?.phoneNumbers, 'fax'),
        eMail: data?.practice?.officeContact?.email
      },
      ptan: data?.prescriber?.physician?.ptanNumber,
      dea: data?.prescriber?.physician?.deaNumber,
      email: data?.prescriber?.physician?.email
    },
    additionalPhysicians: getAdditionalPhysiciansPayload(data?.additionalPhysicians),
    diagnosis: {
      diagnosisCode: data?.clinical?.primaryDiagnosisCode?.split('-')?.[0] || null,
      diagnosisCodesSecondary: data?.clinical?.secondaryDiagnosisCode?.split('-')?.[0] || null,
      diagnosisCodesTertiary: data?.clinical?.tertiaryDiagnosisCode?.split('-')?.[0] || null,
      clinicalPrescribeInitialDose:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.prescribinginformation.InitialDose')
          ?.value ?? null,
      clinicalPrescribeMaintenanceDose:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.prescribinginformation.MaintenanceDose'
        )?.value ?? null,
      clinicalNoOtherMedications:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.NoOtherMedications')?.value ?? null,
      clinicalListOtherMedications:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.ListOtherMedications.Specify')
          ?.value ?? null,
      clinicalAllergiesNo:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.Allergies.No')?.value ?? null,
      listClinicalAllergies:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.Allergies.Specify')?.value ?? null,
      clinicalPrescriptionQuantity:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.Prescription.Quantity')?.value ??
        null,
      clinicalPrescriptionInitialDoseQuantity:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.PiInitialDoseQuantity')?.value ??
        null,
      clinicalPrescriptionMaintenanceQuantity:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.PiMaintenanceDoseQuantity')?.value ??
        null,
      clinicalPrescriptionMaintenanceRefill:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.PiMaintenanceDoseRefills')?.value ??
        null,
      clinicalPrescriptionRefills:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.Prescription.Refills')?.value ??
        null,
      clinicalAlternateDosingInstructions:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.Prescription.AlternateDosingInstructionsSpecify'
        )?.value ?? null,
      clinicalUptraviRefill:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.Prescription.Uptravi.Refill')
          ?.value ?? null,
      clinicalPrescriptionStrength:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.Prescription.Strength')?.value ??
        null,
      clinicalDiagnosisIdiopathic:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.Diagnosis.Opsumit.Primary.Code.Idiopathic'
        )?.value ?? null,
      clinicalDiagnosisHeritable:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.Diagnosis.Opsumit.Primary.Code.Heritable'
        )?.value ?? null,
      uptraviConnectiveTissueDisease:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.Diagnosis.Opsumit.Primary.Code.ConnectiveTissueDisease'
        )?.value ?? null,
      uptraviCongenitalHeartDisease:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.Diagnosis.Opsumit.Primary.Code.CongenitalHeartDisease'
        )?.value ?? null,
      uptraviDrugsToxinsInduced:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.Diagnosis.Opsumit.Primary.Code.DrugsToxinsInduced'
        )?.value ?? null,
      uptraviHIV:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.Diagnosis.Opsumit.Primary.Code.HIV')
          ?.value ?? null,
      uptraviHospitalSettingYes:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'SiteOfInfusion.SiteType.HospitalInpatient.Yes'
        )?.value ?? null,
      uptraviHospitalSettingNo:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'SiteOfInfusion.SiteType.HospitalInpatient.No')
          ?.value ?? null,
      uptraviShipToPatient:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'SiteOfInfusion.ShipTo.Patient')?.value ??
        null,
      uptraviShipToPrescriber:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'SiteOfInfusion.ShipTo.PrescribingMDOffice')
          ?.value ?? null,
      uptraviShipToOther:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'SiteOfInfusion.ShipTo.Other')?.value ?? null,
      opsumitVoucherProgram:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.VoucherProgram')?.value ?? null,
      opsynviVoucherNaiveOptOne:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.VoucherProgram.OptionOne.NaivePatients'
        )?.value ?? null,
      opsynviVoucherNaiveOptTwo:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.VoucherProgram.OptionTwo.NaivePatients'
        )?.value ?? null,
      opsynviVoucherPdeOptOne:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.VoucherProgram.OptionOne.PdePatients'
        )?.value ?? null,
      opsynviVoucherPdeOptTwo:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.VoucherProgram.OptionTwo.PdePatients'
        )?.value ?? null,
      opsynviConnectiveTissueDisease:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.ConnectiveTissueDisease')?.value ??
        null,
      opsynviCongenitalHeartDisease:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.CongenitalHeartDisease')?.value ??
        null,
      opsynviDrugsToxinsInduced:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.DrugsToxinsInduced')?.value ?? null,
      opsynviHIV: data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.HIV')?.value ?? null,
      opsynviUptitrateRefills:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.PiInitialDoseUptitrateRefills')
          ?.value ?? null,
      opsynviUptitrateQuantity:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.PiInitialDoseUptitrateQuantity')
          ?.value ?? null,
      otherLocationName: data?.practices?.find((obj: { role: string }) => obj.role === 'Treating')?.name ?? null,
      otherLocationAddress:
        data?.practices?.find((obj: { role: string }) => obj.role === 'Treating')?.location?.address?.address1 ?? null,
      otherLocationCity:
        data?.practices?.find((obj: { role: string }) => obj.role === 'Treating')?.location?.address?.city ?? null,
      otherLocationState:
        data?.practices?.find((obj: { role: string }) => obj.role === 'Treating')?.location?.address?.state ?? null,
      otherLocationZip:
        data?.practices?.find((obj: { role: string }) => obj.role === 'Treating')?.location?.address?.zip ?? null,
      otherLocationPhone:
        data?.practices
          ?.find((obj: { role: string }) => obj.role === 'Treating')
          ?.location?.phoneNumbers?.find((num: { phoneType: string }) => num.phoneType === 'WORK')?.phoneNumber ?? null,
      clinicalSignatureDate:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.Provider.SignatureDate')?.value ??
        null,
      homeVisitService:
        data?.serviceSpecific?.supportServiceData?.find(
          (obj: { serviceName: string }) => obj.serviceName === 'homevisit'
        )?.value ?? null,
      virtualVisitService:
        data?.serviceSpecific?.supportServiceData?.find(
          (obj: { serviceName: string }) => obj.serviceName === 'virtualvisit'
        )?.value ?? null,
      clinicalOpsIdiopathic:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.Idiopathic')?.value ?? null,
      clinicalOpsHeritable:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.Heritable')?.value ?? null,
      opsynviDosingCheck:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.PiAlternateDosingInstructionsCheckbox'
        )?.value ?? null,
      opsynviDosingInstruction:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.PiAlternateDosingInstructionsText')
          ?.value ?? null,
      opsynviDosingQuantity:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.PiAlternateDosingInstructionsQuantity'
        )?.value ?? null,
      opsynviDosingRefills:
        data?.customFields?.find(
          (obj: { name: string }) => obj.name === 'Clinical.PiAlternateDosingInstructionsRefills'
        )?.value ?? null,
      uptraviNoOtherMedications:
        data?.customFields?.find((obj: { name: string }) => obj.name === 'Clinical.NoOtherMedications.CB')?.value ??
        null,
      medicationNotes: getMedicationNotes(data.customFields),
      clinicalTreatmentDatesFrom: getCustomFieldData(data?.customFields, 'Clinical.clinicalTreatmentDatesFrom'),
      clinicalTreatmentDatesTo: getCustomFieldData(data?.customFields, 'Clinical.clinicalTreatmentDatesTo'),
      hasPatientStartedTreatment:
        getCustomFieldData(data?.customFields, 'Clinical.HasPatientStartedTreatment.Yes') ?? 'No',
      anticipatedDateofTreatment: getCustomFieldData(data?.customFields, 'Clinical.AnicipatedDateofTreatment'),
      priorTherapy: getCustomFieldData(data?.customFields, 'Clinical.clinicalOtherDetails'),
      otherDiagnosisCode: data?.clinical?.otherDiagnosisCode || null,
      prefilledSyringe: getCustomFieldData(data?.customFields, 'Clinical.clinicalDose.PrefilledSyringe') ?? 'No',
      autoinjector: getCustomFieldData(data?.customFields, 'Clinical.clinicalDose.Autoinjector') ?? 'No',
      frequency: getFrequencyData(data?.customFields),
      otherFrequency: getCustomFieldData(data?.customFields, 'Clinical.clinicalDoseFrequencyOther'),
      dispense: getDispenseData(data?.customFields),
      otherDispense: getCustomFieldData(data?.customFields, 'Clinical.clinicalDispenseOther'),
      refills: getCustomFieldData(data?.customFields, 'Clinical.clinicalRefill'),
      patientWeight: getCustomFieldData(data?.customFields, 'Clinical.clinicalPatientWeightkg'),
      patientSigInfuse: getCustomFieldData(data?.customFields, 'Clinical.clinicalPatientSigInfuse'),
      patientInfuse: getCustomFieldData(data?.customFields, 'Clinical.clinicalPatientInfuse'),
      cptCodes: getCustomFieldData(data?.customFields, 'Clinical.cptCodes'),

      acquireThrough: getAcquireThrough(data?.customFields),
      clinicalPreferredSpecialtyPharmacy: getCustomFieldData(
        data?.customFields,
        'Clinical.clinicalPreferredSpecialtyPharmacy'
      ),
      dateOfTreatment: data?.clinical?.dateOfTreatment || null,
      shippingAddressLocation: getShippingAddressLocation(data?.customFields),
      siteInfusionName: getSiteInfusionData('Clinical.SiteInfusion.Name', 'Clinical.InfusionSiteName'),
      siteInfusionTaxId: getCustomFieldData(data?.customFields, 'Clinical.SiteInfusion.TaxId'),
      siteInfusionStreet: getSiteInfusionData('Clinical.SiteInfusion.Street', 'Clinical.InfusionSiteAddress'),
      siteInfusionSuite: getCustomFieldData(data?.customFields, 'Clinical.SiteInfusion.Suite'),
      siteInfusionCity: getSiteInfusionData('Clinical.SiteInfusion.City', 'Clinical.InfusionSiteCity'),
      siteInfusionState: getSiteInfusionData('Clinical.SiteInfusion.State', 'Clinical.InfusionSiteState'),
      siteInfusionZip: getSiteInfusionData('Clinical.SiteInfusion.Zip', 'Clinical.InfusionSiteZip'),
      siteInfusionPhone: getCustomFieldData(data?.customFields, 'Clinical.InfusionSitePhone'),
      prescriptionType: getPrescriptionType(data?.customFields),
      lastDateOfTreatment: getCustomFieldData(data?.customFields, 'Clinical.Prescription.LastDateofTreatment'),
      prescriptionDispense: getPrescriptionDispense(data?.customFields),
      prescriptionDaysSupply: getPrescriptionDaysSupply(data?.customFields),
      prescriptionRefills: getCustomFieldData(data?.customFields, 'Clinical.Prescription.Refill'),
      dosage2Weeks: getDosage2Weeks(data?.customFields),
      dosage4Weeks: getDosage4Weeks(data?.customFields),
      igELevel: getCustomFieldData(data?.customFields, 'Clinical.Diagnosis.IgELevel'),
      weight: patientWeight.weight,
      weightMeasure: patientWeight.weightMeasure
    },
    administrationInformation: data?.administeringInformation
      ? typeof data?.administeringInformation === 'object'
        ? String(JSON.stringify(data?.administeringInformation))?.slice(0, 98)
        : String(data?.administeringInformation)
      : null,
    patientAttestation: {
      attestationType: data?.patientConsent?.attestationType,
      manualAttestationDocument: data?.documents ? getManualAttestation(data?.documents) : {},
      electronicAttestation: {
        patientEmail: data?.patient?.email,
        patientMobileNumber: getPhoneNumber(data?.patient?.phoneNumbers, 'home')
      }
    },
    prescriberAttestation: data?.prescriber?.attestation,
    drugId: drugId,
    caseRecordId: caseId ?? 0,
    enrollmentSignatures: [
      {
        entityType: 'Prescription',
        signaturePurpose: 'PrescriptionAllowed',
        signatureType: data?.enrollmentSignatures?.filter((p: AnyObject) => p.signatureType.toLowerCase() === 'write')
          ? 'Image'
          : data?.enrollmentSignatures?.filter((p: AnyObject) => p.signatureType.toLowerCase() === 'type')
          ? 'Text'
          : '',
        signatureDateTime: data?.enrollmentSignatures?.[0]?.signatureDateTime,
        signatureImage: data?.enrollmentSignatures?.[0]?.signatureImage
      }
    ],
    patientEnrollmentGuid: sessionStorage.getItem('enrollmentModuleEnrollmentId'),
    isEnrollmentUsingModule: isUsingEnrollmentModule,
    isTamTracker: isTamTracker,
    document: doc
  };
};

export const callModuleEnrollmentApi = async (
  accessToken: string | null,
  enrollmentId: string | null,
  practiceId: string | null
) => {
  const headers = new Headers();
  headers.append('Referer', 'test');
  headers.append('Content-Type', 'application/json');
  headers.append('Authorization', `Bearer ${accessToken}`);

  const requestOptions = {
    method: 'GET',
    headers
  };
  return fetch(getEnrollmentModuleAPIEndpoint(enrollmentId, practiceId), requestOptions);
};

export const callModuleEnrollmentPdfApi = async (
  accessToken: string | null,
  enrollmentId: string | null,
  practiceId: string | null
) => {
  const headers = new Headers();
  headers.append('Referer', 'test');
  headers.append('Content-Type', 'application/json');
  headers.append('Authorization', `Bearer ${accessToken}`);

  const requestOptions = {
    method: 'GET',
    headers
  };
  return fetch(getAPIEndpointForEnrollmentModulePDF(enrollmentId, practiceId), requestOptions);
};

export const RequestSubmitView = () => {
  const dispatch = useDispatch();
  const [loadingEnrollment, setLoadingEnrollment] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string>('');
  const { mutate: enroll } = useEnrollment({});
  let status = '';
  let patientId = '';
  const { hash: service, state } = useLocation<AnyObject>();
  const metadata = (rxModulesMetadata as AnyType)[service.replace('#', '')];
  const [enrollmentData, setEnrollmentData] = useState<AnyType>({});

  const sessionDrug = sessionStorage.getItem('drugList');
  const drugList: Drug[] = sessionDrug ? JSON.parse(sessionDrug) : useTenancyDrugListSelector();

  const applicationName = useSelector(clientApplicationNameSelector);
  const autoServiceOption: AnyType = useSelector<AnyType>(tenancyFeatureSelector('autoServiceOption'));

  const isUsingEnrollmentModule = useSelector(
    (state: AppRootState) =>
      (((state.app?.entities?.features as AnyType) ?? {})['isEnrollmentUsingModule'] as AnyType)?.data?.enabled
  );
  const isTamTracker = useSelector((state: AppRootState) => state?.app?.entities?.features?.tamTracker?.data);
  const callEnrollmentAPI = async (payload: AnyObject) => {
    try {
      const result = await enroll(payload);
      if (result?.errors && result?.errors?.length < 1) {
        sessionStorage.setItem('enrollmentModuleEnrollmentId', '');
        sessionStorage.setItem('enrollmentModulePracticeId', '');
        sessionStorage.setItem('caseId', '');
        sessionStorage.setItem('drugList', '');
        sessionStorage.setItem('locationId', '');
        sessionStorage.setItem('patientIdEnrollment', '');
        sessionStorage.setItem('caseIdEnrollment', '');
      }
    } catch (e) {
      throw e?.message;
    }
  };

  const getDrugId = (jsonDrug: any) => {
    if (!jsonDrug) return null;
    const selectedDrug = drugList.find((element: Drug) => element?.JCode1 === jsonDrug?.jCode ?? 0);
    return selectedDrug
      ? selectedDrug?.DrugId
      : drugList.find((element: Drug) => element?.DrugNDC === jsonDrug?.ndc ?? 0)?.DrugId;
  };

  const loadAccessToken = useCallback(async () => {
    const token = await getToken(ENROLLMENT_MODULE_TRIGGER_RULE);
    const result = await token.text();

    try {
      const json = JSON.parse(result);
      setAccessToken(json.authToken);
    } catch (e) {
      setLoadingEnrollment(false);
      throw e?.message;
    }
  }, []);
  useEffect(() => {
    if (accessToken && loadingEnrollment && !state?.enrollmentJson?.enrollmentId) {
      loadPayloadData(accessToken);
    }
  }, [accessToken]);

  const loadPayloadData = useCallback(async (accessToken: string) => {
    if (!accessToken) return;

    const response = await callModuleEnrollmentApi(
      accessToken,
      sessionStorage.getItem('enrollmentModuleEnrollmentId'),
      sessionStorage.getItem('enrollmentModulePracticeId')
    );
    const result = await response.text();

    try {
      const json = JSON.parse(result);
      const drugId = getDrugId(json?.drug);
      setEnrollmentData(json);
      callEnrollmentAPI(
        prepareEnollmentPayload(json, 0, null, Number(drugId) ?? 0, json?.enrollmentDocument, isUsingEnrollmentModule)
      );
    } catch (e) {
      throw e?.message;
    } finally {
      setLoadingEnrollment(false);
    }
  }, []);

  if (metadata) {
    const routeData = useLocation<AnyType>();
    const physicianNpi = routeData.state?.physicianNpiValue;
    patientId = routeData.state?.patientIdValue;
    const { data } = useListRequest({
      endDate: moment().add(1, 'day').utc().toString(),
      externalPatientId: patientId,
      physicianNpi: physicianNpi || '',
      startDate: moment().subtract(2, 'days').utc().toString(),
      abbreviation: metadata.abbreviation || ''
    });

    status = data?.[0]?.Status;
  }
  useEffect(() => {
    if (
      sessionStorage.getItem('enrollmentModuleEnrollmentId') &&
      sessionStorage.getItem('enrollmentModulePracticeId') &&
      isUsingEnrollmentModule
    ) {
      setLoadingEnrollment(true);
      loadAccessToken();
    }
  }, []);

  if (loadingEnrollment) {
    return (
      <div className='text-center p-5'>
        <LoadingIndicator />
        <p>Please wait as we process your Enrollment</p>
      </div>
    );
  }

  return (
    <PrivateLayout pageTitle='submission successful'>
      <Col className='mb-3'>
        <RequestSubmitMessage
          enrollmentData={
            Object.keys(state?.enrollmentJson ?? {}).length > 0
              ? state?.enrollmentJson
              : Object.keys(enrollmentData).length === 0
              ? state
              : enrollmentData
          }
        />
      </Col>

      <Row>
        {renderSwitch(status) === ServiceOptionType.MedicalElectronicPriorAuthorization && (
          <Col lg={3}>
            <Button
              variant='secondary'
              size='sm'
              className='px-4'
              onClick={() =>
                dispatch(
                  navigateToServiceRequestSelected(ServiceOptionType.MedicalElectronicPriorAuthorization, patientId)
                )
              }>
              START MEDICAL PRIOR AUTHORIZATION
            </Button>
          </Col>
        )}
        {renderSwitch(status) === ServiceOptionType.PharmacyElectronicPriorAuthorization && (
          <Col lg={3}>
            <Button
              variant='secondary'
              size='sm'
              className='px-4'
              onClick={() =>
                dispatch(
                  navigateToServiceRequestSelected(ServiceOptionType.PharmacyElectronicPriorAuthorization, patientId)
                )
              }>
              START PRIOR AUTHORIZATION
            </Button>
          </Col>
        )}
        {!autoServiceOption?.enabled && (
          <Col lg={3}>
            <Button
              variant='secondary'
              size='sm'
              className='px-4'
              onClick={() => {
                if (applicationName === 'PhyzProviderPortal') {
                  dispatch(navigateToCases());
                } else {
                  dispatch(navigateToServiceRequest());
                }
              }}>
              START NEW REQUEST
            </Button>
          </Col>
        )}
        {!autoServiceOption?.enabled && (
          <Col xs={3}>
            <Button variant='outline-secondary' size='sm' className='px-5' onClick={() => dispatch(navigateToHome())}>
              CLOSE
            </Button>
          </Col>
        )}
      </Row>
    </PrivateLayout>
  );
};
